// src/pages/Documents/List.jsx
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Container, Alert, Modal,
  Form, Row, Col, Badge, Spinner,
  InputGroup, Pagination, Card
} from 'react-bootstrap';
import { documentAPI } from '../../api/wbuoaApi';
import { useAuth } from '../../context/AuthContext';
import {
  FaEdit, FaTrash, FaUpload, FaDownload,
  FaSearch, FaFilter, FaSort, FaSortUp, FaSortDown,
  FaFilePdf, FaFileWord, FaFileExcel, FaFileImage,
  FaFileAlt, FaArchive, FaBoxOpen
} from 'react-icons/fa';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const DocumentList = () => {
  // Get auth context
  const { user } = useAuth();

  // Extract user data properly from the nested structure
  const userData = user?.data || user;
  const userRole = userData?.role || 'user';

  // Check if current user is admin
  const isAdmin = userRole === 'admin';

  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    category_id: '',
    search_text: '',
    file: null,
    file_name: '',
    archive: 0 // Default to 0 (No/Active)
  });
  const [formErrors, setFormErrors] = useState({});

  // Search and Filter States
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'upload_date', direction: 'desc' });
  const [archiveFilter, setArchiveFilter] = useState('all');

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [documents, searchTerm, selectedCategory, archiveFilter, sortConfig, currentPage, itemsPerPage]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await documentAPI.listDocuments();
      console.log('Documents API Response:', response);

      // Handle different response structures
      const docs = response.data || response || [];
      setDocuments(docs);
      setTotalItems(docs.length);
      setTotalPages(Math.ceil(docs.length / itemsPerPage));
      setError(null);
    } catch (err) {
      console.error('Fetch documents error:', err);
      setError('Failed to fetch documents: ' + (err.message || 'Server error'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await documentAPI.listCategories();
      console.log('Categories API Response:', response);

      // Handle different response structures
      const categoriesData = response.data || response || [];
      setCategories(categoriesData);

      // Set default category if available
      if (categoriesData.length > 0 && !formData.category_id) {
        setFormData(prev => ({
          ...prev,
          category_id: categoriesData[0].id
        }));
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError('Failed to fetch document categories');
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(doc =>
        doc.file_name?.toLowerCase().includes(term) ||
        doc.description?.toLowerCase().includes(term) ||
        doc.search_text?.toLowerCase().includes(term) ||
        doc.category?.toLowerCase().includes(term)
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(doc =>
        doc.category === selectedCategory
      );
    }

    // Apply archive filter
    if (archiveFilter !== 'all') {
      const archiveValue = archiveFilter === 'archived' ? 1 : 0;
      filtered = filtered.filter(doc => doc.archive === archiveValue);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      if (sortConfig.key) {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle date fields
        if (sortConfig.key === 'upload_date') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const paginated = filtered.slice(startIndex, startIndex + itemsPerPage);

    setFilteredDocuments(paginated);
    setTotalItems(filtered.length);
    setTotalPages(Math.ceil(filtered.length / itemsPerPage));
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return <FaFileAlt />;

    if (fileType.includes('pdf')) return <FaFilePdf className="text-danger" />;
    if (fileType.includes('word') || fileType.includes('doc')) return <FaFileWord className="text-primary" />;
    if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return <FaFileExcel className="text-success" />;
    if (fileType.includes('image') || fileType.includes('jpg') || fileType.includes('jpeg') || fileType.includes('png')) return <FaFileImage className="text-warning" />;

    return <FaFileAlt />;
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.category_id) {
      errors.category_id = 'Category is required';
    }

    if (!formData.description) {
      errors.description = 'Description is required';
    }

    if (!isEditing && !formData.file) {
      errors.file = 'File is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleShowModal = (document = null) => {
    setFormErrors({});
    if (document) {
      // Editing existing document - find the category in categories array
      const categoryObj = categories.find(cat =>
        cat.category_name === document.category ||
        cat.id === document.category_id
      );

      setSelectedDocument(document);
      setIsEditing(true);
      setFormData({
        description: document.description || '',
        category_id: categoryObj?.id || document.category_id || '',
        search_text: document.search_text || '',
        file: null,
        file_name: document.file_name || '',
        archive: document.archive || 0
      });
    } else {
      // Creating new document
      setSelectedDocument(null);
      setIsEditing(false);
      setFormData({
        description: '',
        category_id: categories.length > 0 ? categories[0].id : '',
        search_text: '',
        file: null,
        file_name: '',
        archive: 0 // Default to 0 (No/Active)
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDocument(null);
    setIsEditing(false);
    setFormData({
      description: '',
      category_id: categories.length > 0 ? categories[0].id : '',
      search_text: '',
      file: null,
      file_name: '',
      archive: 0
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };

  const handleCategoryChange = (e) => {
    const selectedId = e.target.value;
    console.log('Category selected - ID:', selectedId);

    setFormData({
      ...formData,
      category_id: selectedId
    });

    if (formErrors.category_id) {
      setFormErrors({
        ...formErrors,
        category_id: ''
      });
    }
  };

  const handleArchiveChange = (e) => {
    // Convert string value to number (1 for Yes/Archived, 0 for No/Active)
    const archiveValue = parseInt(e.target.value, 10);
    setFormData({
      ...formData,
      archive: archiveValue
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        e.target.value = null;
        return;
      }

      setFormData({
        ...formData,
        file: file,
        file_name: file.name
      });

      if (formErrors.file) {
        setFormErrors({
          ...formErrors,
          file: ''
        });
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Log the form data before submission for debugging
    console.log('Form Data before submission:', {
      category_id: formData.category_id,
      description: formData.description,
      search_text: formData.search_text,
      archive: formData.archive,
      file: formData.file ? formData.file.name : null
    });

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const submitData = new FormData();

      if (isEditing && selectedDocument) {
        // Update existing document
        submitData.append('category_id', formData.category_id);
        submitData.append('description', formData.description);
        submitData.append('search_text', formData.search_text || '');
        submitData.append('archive', formData.archive.toString());

        // Append file if present (for replacement)
        if (formData.file) {
          submitData.append('file', formData.file);
        }

        console.log('Updating document with data:');
        for (let pair of submitData.entries()) {
          console.log(pair[0] + ': ' + (pair[0] === 'file' ? 'File: ' + formData.file.name : pair[1]));
        }

        const response = await documentAPI.updateDocument(selectedDocument.id, submitData);
        console.log('Update response:', response);

        if (response && response.success) {
          setSuccess('Document updated successfully!');
        } else {
          throw new Error(response?.message || 'Update failed');
        }
      } else {
        // Create new document - exactly matching the API call structure
        // IMPORTANT: Make sure category_id is a string and not empty
        if (!formData.category_id) {
          throw new Error('Category ID is missing');
        }

        submitData.append('category_id', formData.category_id.toString());
        submitData.append('description', formData.description);
        submitData.append('search_text', formData.search_text || '');
        submitData.append('archive', formData.archive.toString());

        // IMPORTANT: Append the file with the correct field name 'file'
        if (formData.file) {
          submitData.append('file', formData.file);
        }

        console.log('Creating document with data:');
        for (let pair of submitData.entries()) {
          console.log(pair[0] + ': ' + (pair[0] === 'file' ? 'File: ' + formData.file.name : pair[1]));
        }

        const response = await documentAPI.uploadDocument(submitData);
        console.log('Upload response:', response);

        if (response && response.success) {
          setSuccess('Document uploaded successfully!');
        } else {
          throw new Error(response?.message || 'Upload failed');
        }
      }

      // Refresh the document list
      await fetchDocuments();

      // Close modal after a short delay to show success message
      setTimeout(() => {
        handleCloseModal();
      }, 1500);

    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.message || err.message || 'Failed to process document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteClick = (document) => {
    setSelectedDocument(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await documentAPI.deleteDocument(selectedDocument.id);
      console.log('Delete response:', response);

      if (response && response.success) {
        setSuccess('Document deleted successfully!');
        await fetchDocuments();
      } else {
        throw new Error(response?.message || 'Delete failed');
      }

      setShowDeleteModal(false);
      setSelectedDocument(null);
    } catch (err) {
      setError('Failed to delete document: ' + (err.message || 'Server error'));
    }
  };

  const handleDownload = (filePath, fileName) => {
    try {
      // Construct full URL for download
      const fullUrl = `${API_BASE_URL}/public/${filePath}`;

      // Create a temporary anchor element
      const link = document.createElement('a');
      link.href = fullUrl;
      link.setAttribute('download', fileName || 'document');
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to download document');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch {
      return dateString;
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setArchiveFilter('all');
    setSortConfig({ key: 'upload_date', direction: 'desc' });
    setCurrentPage(1);
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxPageNumbers = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
    let endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);

    if (endPage - startPage + 1 < maxPageNumbers) {
      startPage = Math.max(1, endPage - maxPageNumbers + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="text-muted">
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} documents
        </div>
        <Pagination className="mb-0">
          <Pagination.First
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          />

          {startPage > 1 && (
            <>
              <Pagination.Item onClick={() => setCurrentPage(1)}>1</Pagination.Item>
              {startPage > 2 && <Pagination.Ellipsis disabled />}
            </>
          )}

          {pageNumbers.map(number => (
            <Pagination.Item
              key={number}
              active={number === currentPage}
              onClick={() => setCurrentPage(number)}
            >
              {number}
            </Pagination.Item>
          ))}

          {endPage < totalPages && (
            <>
              {endPage < totalPages - 1 && <Pagination.Ellipsis disabled />}
              <Pagination.Item onClick={() => setCurrentPage(totalPages)}>
                {totalPages}
              </Pagination.Item>
            </>
          )}

          <Pagination.Next
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          />
          <Pagination.Last
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          />
        </Pagination>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted">Items per page:</span>
          <Form.Select
            size="sm"
            style={{ width: '80px' }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
          </Form.Select>
        </div>
      </div>
    );
  };

  return (
    <Container fluid className="py-4">
      {/* Header Section */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="mb-1">Document Management</h2>
              <p className="text-muted mb-0">
                Upload, manage, and organize your documents
              </p>
              {!isAdmin && (
                <Badge bg="info" className="mt-2">
                  Viewing as: {userRole} (Can Upload but Cannot Edit/Delete)
                </Badge>
              )}
            </div>
            {/* Upload button visible for all users */}
            <Button variant="primary" onClick={() => handleShowModal()}>
              <FaUpload className="me-2" /> Upload Document
            </Button>
          </div>

          {/* Search and Filter Section */}
          <Row className="g-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search documents by file name, description, or category..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setSearchTerm('')}
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={2}>
              <Form.Select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.category_name}>
                    {cat.category_name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Select
                value={archiveFilter}
                onChange={(e) => {
                  setArchiveFilter(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Button
                variant="outline-secondary"
                onClick={resetFilters}
                className="w-100"
              >
                <FaFilter className="me-2" />
                Reset Filters
              </Button>
            </Col>
          </Row>

          {/* Stats */}
          <Row className="mt-4">
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Total Documents</h6>
                      <h4 className="mb-0">{totalItems}</h4>
                    </div>
                    <div className="text-primary">
                      <FaUpload size={24} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Active</h6>
                      <h4 className="mb-0">
                        {documents.filter(d => d.archive === 0).length}
                      </h4>
                    </div>
                    <div className="text-success">
                      <Badge bg="success">Active</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Archived</h6>
                      <h4 className="mb-0">
                        {documents.filter(d => d.archive === 1).length}
                      </h4>
                    </div>
                    <div className="text-secondary">
                      <Badge bg="secondary">Archived</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="py-3">
                  <div className="d-flex justify-content-between">
                    <div>
                      <h6 className="text-muted mb-0">Total Size</h6>
                      <h4 className="mb-0">
                        {formatFileSize(documents.reduce((sum, doc) => sum + (doc.file_size || 0), 0))}
                      </h4>
                    </div>
                    <div className="text-info">
                      <Badge bg="info">Size</Badge>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-4">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error}</p>
        </Alert>
      )}

      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-4">
          <Alert.Heading>Success</Alert.Heading>
          <p>{success}</p>
        </Alert>
      )}

      {/* Documents Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading documents...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => requestSort('file_name')}>
                        <div className="d-flex align-items-center gap-1">
                          File Name
                          {getSortIcon('file_name')}
                        </div>
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => requestSort('category')}>
                        <div className="d-flex align-items-center gap-1">
                          Category
                          {getSortIcon('category')}
                        </div>
                      </th>
                      <th>Description</th>
                      <th style={{ cursor: 'pointer' }} onClick={() => requestSort('file_size')}>
                        <div className="d-flex align-items-center gap-1">
                          Size
                          {getSortIcon('file_size')}
                        </div>
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => requestSort('upload_date')}>
                        <div className="d-flex align-items-center gap-1">
                          Uploaded
                          {getSortIcon('upload_date')}
                        </div>
                      </th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDocuments.length > 0 ? (
                      filteredDocuments.map(doc => (
                        <tr key={doc.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="me-2">
                                {getFileIcon(doc.file_type)}
                              </div>
                              <div
                                className="text-truncate"
                                style={{ maxWidth: '180px' }}
                                title={doc.file_name}
                              >
                                {doc.file_name}
                              </div>
                            </div>
                          </td>
                          <td>
                            <Badge bg="info">{doc.category || 'Uncategorized'}</Badge>
                          </td>
                          <td>
                            <div
                              className="text-truncate"
                              style={{ maxWidth: '200px' }}
                              title={doc.description}
                            >
                              {doc.description}
                            </div>
                          </td>
                          <td>{formatFileSize(doc.file_size)}</td>
                          <td>{formatDate(doc.upload_date)}</td>
                          <td>
                            <Badge bg={doc.archive === 1 ? 'secondary' : 'success'}>
                              {doc.archive === 1 ? 'Archived' : 'Active'}
                            </Badge>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleDownload(doc.file_path, doc.file_name)}
                                title="Download"
                              >
                                <FaDownload />
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => handleShowModal(doc)}
                                    title="Edit"
                                  >
                                    <FaEdit />
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteClick(doc)}
                                    title="Delete"
                                  >
                                    <FaTrash />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-5">
                          <div className="py-4">
                            <FaSearch size={48} className="text-muted mb-3" />
                            <h5>No documents found</h5>
                            <p className="text-muted">
                              {searchTerm || selectedCategory !== 'all' || archiveFilter !== 'all'
                                ? 'Try changing your search or filters'
                                : 'Upload your first document to get started'}
                            </p>
                            {searchTerm || selectedCategory !== 'all' || archiveFilter !== 'all' ? (
                              <Button variant="outline-primary" onClick={resetFilters}>
                                Reset Filters
                              </Button>
                            ) : (
                              <Button variant="primary" onClick={() => handleShowModal()}>
                                <FaUpload className="me-2" /> Upload Document
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {filteredDocuments.length > 0 && totalPages > 1 && (
                <div className="p-3 border-top">
                  {renderPagination()}
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Upload/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? 'Edit Document' : 'Upload New Document'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Select
                    value={formData.category_id}
                    onChange={handleCategoryChange}
                    isInvalid={!!formErrors.category_id}
                    required
                  >
                    <option value="">Select a category</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.category_name}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.category_id}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Search Keywords</Form.Label>
                  <Form.Control
                    type="text"
                    name="search_text"
                    value={formData.search_text}
                    onChange={handleInputChange}
                    placeholder="Enter search keywords"
                  />
                  <Form.Text className="text-muted">
                    Example: "order, circular, 2025, guidelines"
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter document description"
                isInvalid={!!formErrors.description}
                required
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.description}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Archive Dropdown - Now available in both Create and Edit modes */}
            <Form.Group className="mb-3">
              <Form.Label>Archive Status</Form.Label>
              <Form.Select
                value={formData.archive}
                onChange={handleArchiveChange}
              >
                <option value={0}>No (Active)</option>
                <option value={1}>Yes (Archived)</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Select "Yes" to archive this document, "No" to keep it active
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {isEditing ? 'Replace File (Optional)' : 'File *'}
              </Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileChange}
                isInvalid={!!formErrors.file}
                required={!isEditing}
                accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
              />
              {formData.file_name && (
                <div className="mt-2">
                  <Badge bg="info">
                    Selected: {formData.file_name}
                  </Badge>
                </div>
              )}
              <Form.Text className="text-muted">
                Max file size: 10MB. Supported formats: PDF, DOC, DOCX, TXT, XLS, XLSX, PPT, PPTX, JPG, PNG
              </Form.Text>
              <Form.Control.Feedback type="invalid">
                {formErrors.file}
              </Form.Control.Feedback>
            </Form.Group>

            {isEditing && selectedDocument && (
              <Alert variant="info">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Current File:</strong> {selectedDocument.file_name}
                    <br />
                    <small>Size: {formatFileSize(selectedDocument.file_size)} | Uploaded: {formatDate(selectedDocument.upload_date)}</small>
                    <br />
                    <small>Current Status: <Badge bg={selectedDocument.archive === 1 ? 'secondary' : 'success'}>
                      {selectedDocument.archive === 1 ? 'Archived' : 'Active'}
                    </Badge></small>
                  </div>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleDownload(selectedDocument.file_path, selectedDocument.file_name)}
                  >
                    <FaDownload /> Download
                  </Button>
                </div>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={uploading}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={uploading}>
              {uploading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {isEditing ? 'Updating...' : 'Uploading...'}
                </>
              ) : (
                <>
                  {isEditing ? 'Update Document' : 'Upload Document'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <h6 className="alert-heading">Warning!</h6>
            <p className="mb-0">
              Are you sure you want to delete the document "<strong>{selectedDocument?.file_name}</strong>"?
              <br />
              This action cannot be undone.
            </p>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm}>
            Delete Document
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DocumentList;